import {
  Box,
  Button,
  ColorType,
  Flex,
  InputGroup,
  Link,
  Text,
} from '@clear/design-system'
import { Map } from '@components/storybook'
import { EnrollmentLocationId, LocationError } from '@pages/enroll/locations'
import { LAYOUT_BREAKPOINT } from '@styles/theme'
import {
  EnrollmentLocation,
  LocationCoordinates,
} from '@utils/getNearbyEnrollmentLocations'
import classNames from 'classnames'
import React, { ChangeEvent, useEffect, useState } from 'react'
import useMedia from 'use-media'
import { GeoLocate } from '../GeoLocate'
import { LocationInfo } from '../LocationInfo'

interface V2LocationsSearchProps {
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  handleSearch: (searchCriteria: string) => Promise<void>
  updateSearchBar: (e: ChangeEvent<HTMLInputElement>) => void
  searchInput: string
  geoLocateSuccessHandler: (position: GeolocationPosition) => void
  mapSearchError: boolean
  locationsWithinRange: EnrollmentLocation[]
  errors: LocationError
  isLoaded: boolean
  searchLocation: LocationCoordinates
  determineMapZoomLevel: (locationsWithinRange: EnrollmentLocation[]) => number
  isSearchComplete: boolean
  defaultZip?: string
  enrollmentLocationId?: string
  onSubmit?: ({ enrollmentLocationId }: EnrollmentLocationId) => Promise<void>
  handleSelect?: (location: string) => void
  selectionKey?: 'locationCode' | 'serviceTerritoryId'
  shouldAutoSelectLocation?: boolean
}

const V2LocationsSearch = ({
  handleKeyDown,
  handleSearch,
  updateSearchBar,
  searchInput,
  geoLocateSuccessHandler,
  mapSearchError,
  locationsWithinRange,
  errors,
  isLoaded,
  searchLocation,
  determineMapZoomLevel,
  isSearchComplete,
  handleSelect,
  enrollmentLocationId,
  defaultZip,
  selectionKey = 'locationCode',
  shouldAutoSelectLocation = true,
}: V2LocationsSearchProps) => {
  const isDesktopLayout = useMedia({ minWidth: LAYOUT_BREAKPOINT })

  const [hasPerformedInitialSearch, setHasPeformedInitialSearch] =
    useState(false)
  const [hasMadeInitialSelection, setHasMadeInitialSelection] = useState(false)
  const [selectedLocation, setSelectedLocation] = useState<string>('')

  useEffect(() => {
    if (isLoaded && !searchInput && !hasPerformedInitialSearch && defaultZip) {
      handleSearch(defaultZip)
      setHasPeformedInitialSearch(true)
    }

    if (
      locationsWithinRange.length > 0 &&
      !hasMadeInitialSelection &&
      shouldAutoSelectLocation
    ) {
      const { locationCode } = locationsWithinRange[0]
      handleSelect?.(locationCode)
      setSelectedLocation(locationCode)

      setHasMadeInitialSelection(true)
    }
  }, [
    defaultZip,
    handleSearch,
    handleSelect,
    hasMadeInitialSelection,
    hasPerformedInitialSearch,
    isLoaded,
    locationsWithinRange,
    searchInput,
    setSelectedLocation,
    shouldAutoSelectLocation,
  ])

  return (
    <>
      <Flex
        alignItems="center"
        justifyContent="center"
        mb={7}
        width="full"
        height={isDesktopLayout ? '5rem' : ''}
        flexDirection={['column', 'row']}
      >
        <InputGroup
          icon="search"
          onKeyDown={(e) => {
            handleKeyDown(e)
          }}
          variant="default"
          id="location-input"
          onChange={updateSearchBar}
          value={searchInput}
          placeholder="Zip Code / City / Airport Code"
          autoComplete="off"
          data-testid="location-search-bar"
          data-cy="searchBar"
        />
        <Button
          text="Search"
          bg={'dhsBorderBlue' as ColorType}
          borderRadius="0 0.25rem 0.25rem 0"
          sx={{
            marginTop: '0.5rem !important',
            marginBottom: `${isDesktopLayout ? '' : '1rem'}`,
            marginLeft: `${isDesktopLayout ? '-1rem' : '0'}`,
          }}
          textProps={{
            variant: 'regular',
            fontSize: '1.25rem',
            fontWeight: '500',
          }}
          onClick={() => handleSearch(searchInput)}
          data-cy="location-search-button"
        />
        <GeoLocate
          onSuccessHandler={geoLocateSuccessHandler}
          linkText="Use Current Location"
        />
      </Flex>
      {mapSearchError ? (
        <Box as="p" textAlign="center" color="utilityDarkErrorRed">
          Sorry, that's not a valid search. Please try again.
        </Box>
      ) : null}
      <Box as="p" textAlign="center" color="utilityDarkErrorRed">
        {locationsWithinRange.length === 0
          ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            errors?.[enrollmentLocationId!]?.length
          : null}
      </Box>

      {locationsWithinRange?.length ? (
        <Flex
          data-testid="found-locations"
          flexDirection={['column-reverse', 'row']}
        >
          <Box
            width={['full', '55%']}
            mr={4}
            height="500px"
            overflowY="scroll"
            pr={4}
            marginTop={isDesktopLayout ? '' : '12'}
          >
            {locationsWithinRange.map(
              (location: EnrollmentLocation, i: number) => {
                return (
                  <Box
                    key={location?.[selectionKey]}
                    data-value={location?.[selectionKey]}
                    className={classNames(
                      'p-4',
                      `${
                        selectedLocation === location.locationCode
                          ? 'border border-dhs-blue rounded solid 1px'
                          : ''
                      }`
                    )}
                    onClick={(e) => {
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      handleSelect?.(e.currentTarget.dataset.value!)
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      setSelectedLocation(e.currentTarget.dataset.value!)
                    }}
                    role="radio"
                  >
                    <Box mt={1} minWidth="1.25rem" maxWidth="350px">
                      <div className="h-1/2" />
                      {location && (
                        <LocationInfo
                          key={location?.locationCode}
                          index={i + 1}
                          location={location}
                        />
                      )}
                    </Box>
                  </Box>
                )
              }
            )}
          </Box>
          {isLoaded && searchLocation ? (
            <Box
              width={['full', '50%']}
              height={['400px', '500px']}
              mb={[6, 0]}
            >
              <Map
                center={searchLocation}
                zoom={determineMapZoomLevel(locationsWithinRange)}
                locationMarkers={locationsWithinRange}
                selectedLocation={selectedLocation}
                full
              />
            </Box>
          ) : (
            <>Loading google maps</>
          )}
        </Flex>
      ) : null}

      {!locationsWithinRange.length &&
      searchLocation &&
      !mapSearchError &&
      isSearchComplete ? (
        <Flex alignItems="start" ml={4} mt={3}>
          <Box as="img" src="/map-pin-not-found.svg" alt="pin-not-found" />
          <Box ml={3}>
            <Text fontSize={4} display="block" mb={1}>
              No locations found
            </Text>
            <Text fontSize={1}>
              Please adjust your search for better results.
            </Text>
          </Box>
        </Flex>
      ) : null}

      {locationsWithinRange && locationsWithinRange[0]?.distance > 40 && (
        <Flex
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          my={6}
        >
          <Text fontSize={4} display="block" mb={1}>
            Need a closer location?
          </Text>
          <Text fontSize={2}>
            Visit the{' '}
            <Link
              target="_blank"
              href="https://www.tsa.gov/precheck/enrollment-centers"
            >
              TSA website
            </Link>{' '}
            for more enrollment location options.
          </Text>
        </Flex>
      )}
    </>
  )
}

export default V2LocationsSearch
